import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { Bot, X } from "lucide-react";
import "./chatbot.css";

const Chatbot = () => {
  const [userMessage, setUserMessage] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingQuestions, setPendingQuestions] = useState(new Set());
  const [messageCounter, setMessageCounter] = useState(0);
  const chatRef = useRef(null);
  const endOfChatRef = useRef(null);

  // Récupérer les données utilisateur depuis Redux
  const userData = useSelector((state) => state.userReducer);

  // Fonction pour convertir les liens en éléments cliquables
  const formatTextWithLinks = (text) => {
    // URLs absolues (https://…) ET liens internes vers une formation (/formations/<id>)
    const urlRegex = /(https?:\/\/[^\s]+|\/formations\/\d+[^\s]*)/g;
    const parts = text.split(urlRegex);

    return parts.map((part, index) => {
      if (part && part.match(urlRegex)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="chat-link"
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };
  // Fonction pour demander l'ajout d'une question à la base de données
  const handleRequestQuestionAdd = async (originalQuestion) => {
    try {
      // Marquer cette question comme demandée pour éviter les doublons
      setPendingQuestions((prev) => new Set([...prev, originalQuestion]));

      // Envoyer la question au backend avec les données utilisateur de Redux
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/chatbot/questions-en-attente`,
        {
          question: originalQuestion,
          name: userData?.name || null,
          firstname: userData?.firstname || null,
        }
      );

      // Afficher un message de confirmation personnalisé
      const confirmationMessage = {
        id: `msg_${messageCounter}`,
        type: "bot",
        text: `✅ Merci ${
          userData?.firstname ? userData.firstname + " " : ""
        }! Votre demande a été transmise avec vos informations. Notre équipe va examiner votre question et l'ajoutera à notre base de connaissances si nécessaire.`,
        isConfirmation: true,
      };

      setChatHistory((prevHistory) => [...prevHistory, confirmationMessage]);
      setMessageCounter((prev) => prev + 1);
    } catch (error) {
      console.error("Erreur lors de la demande d'ajout :", error);
      const errorMessage = {
        id: `msg_${messageCounter}`,
        type: "bot",
        text: "Désolé, une erreur est survenue lors de l'envoi de votre demande. Veuillez réessayer.",
      };
      setChatHistory((prevHistory) => [...prevHistory, errorMessage]);
      setMessageCounter((prev) => prev + 1);
    }
  };
  const handleSendMessage = async () => {
    if (userMessage.trim() === "") return;

    const currentQuestion = userMessage.trim();
    const newMessage = {
      id: `msg_${messageCounter}`,
      type: "user",
      text: currentQuestion,
    };
    setChatHistory([...chatHistory, newMessage]);
    setMessageCounter((prev) => prev + 1);
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/chatbot/ask`,
        {
          question: currentQuestion,
          name: userData?.name || null,
          firstname: userData?.firstname || null,
        }
      );
      const botResponse = response.data.reponse; // Vérifier si c'est une réponse indiquant qu'on n'a pas la réponse
      const isNoAnswerResponse =
        botResponse.includes(
          "Je ne trouve pas cette information dans notre base de données"
        ) ||
        botResponse.includes("n'avons pas encore eu cette question") ||
        botResponse.includes("transmise à notre équipe") ||
        botResponse.includes("ne donne pas de détails") ||
        botResponse.includes("ne trouve pas d'information") ||
        botResponse.includes("ne peut pas répondre") ||
        botResponse.includes("Désolé, mais l'information fournie") ||
        botResponse.includes("sans plus d'informations spécifiques") ||
        botResponse.includes(
          "il est difficile de fournir une réponse précise"
        ) ||
        (botResponse.toLowerCase().includes("désolé") &&
          (botResponse.toLowerCase().includes("ne") ||
            botResponse.toLowerCase().includes("pas")));

      const botMessage = {
        id: `msg_${messageCounter + 1}`,
        type: "bot",
        text: botResponse,
        isNoAnswer: isNoAnswerResponse,
        originalQuestion: isNoAnswerResponse ? currentQuestion : null,
      };
      setChatHistory((prevHistory) => [...prevHistory, botMessage]);
      setMessageCounter((prev) => prev + 2);
    } catch (error) {
      console.error("Erreur lors de la communication avec le chatbot :", error);
      const errorMessage = {
        id: `msg_${messageCounter + 1}`,
        type: "bot",
        text: "Désolé, une erreur est survenue. Veuillez réessayer.",
      };
      setChatHistory((prevHistory) => [...prevHistory, errorMessage]);
      setMessageCounter((prev) => prev + 2);
    }

    setIsLoading(false);
    setUserMessage("");
  };

  const handleInputChange = (e) => {
    setUserMessage(e.target.value);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSendMessage();
    }
  };

  const toggleChatWindow = () => {
    setIsOpen(!isOpen);
  };

  const handleClickOutside = (e) => {
    if (chatRef.current && !chatRef.current.contains(e.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (endOfChatRef.current) {
      endOfChatRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatHistory]);

  return (
    <div className="chatbot-wrapper">
      <button
        className={`chatbot-bubble ${isOpen ? "open" : ""}`}
        onClick={toggleChatWindow}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleChatWindow();
          }
        }}
        aria-label={isOpen ? "Fermer le chatbot" : "Ouvrir le chatbot"}
        aria-expanded={isOpen}
      >
        {isOpen ? <X size={24} /> : <Bot size={28} />}
      </button>{" "}
      <div
        className={`chatbot-container ${isOpen ? "open" : "closed"}`}
        ref={chatRef}
      >
        <div className="chatbot-header">
          <div className="chatbot-header-content">
            <div className="chatbot-avatar">
              <Bot size={20} />
            </div>
            <div className="chatbot-header-text">
              <h3>Assistant Skilly</h3>
              <span>En ligne</span>
            </div>
          </div>
          <button
            className="chatbot-close-btn"
            onClick={toggleChatWindow}
            aria-label="Fermer le chatbot"
          >
            <X size={16} />
          </button>
        </div>
        <div className="chat-history">
          {chatHistory.map((message) => (
            <div
              key={message.id}
              className={`chat-message ${message.type} ${
                message.isNoAnswer ? "no-answer" : ""
              } ${message.isConfirmation ? "confirmation" : ""}`}
            >
              {" "}
              {message.isNoAnswer ? (
                <div className="no-answer-content">
                  <div className="no-answer-icon">❓</div>
                  <div className="no-answer-text">
                    <p>{formatTextWithLinks(message.text)}</p>
                    <div className="no-answer-info">
                      <i className="fa-solid fa-info-circle"></i>
                      <span>
                        Votre question sera traitée par notre équipe et ajoutée
                        à notre base de connaissances.
                      </span>
                    </div>
                    {message.originalQuestion &&
                      !pendingQuestions.has(message.originalQuestion) && (
                        <div className="no-answer-actions">
                          <button
                            className="request-add-btn"
                            onClick={() =>
                              handleRequestQuestionAdd(message.originalQuestion)
                            }
                            aria-label="Demander l'ajout de cette question à la base de données"
                          >
                            📋 Ajouter cette question à la base de connaissances
                          </button>
                        </div>
                      )}
                    {pendingQuestions.has(message.originalQuestion) && (
                      <div className="no-answer-requested">
                        ✅ Question transmise à l'équipe
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                formatTextWithLinks(message.text)
              )}
            </div>
          ))}

          {isLoading && (
            <div className="chat-message bot">
              🤖 est en train de réfléchir ...
            </div>
          )}
          <div ref={endOfChatRef}></div>
        </div>
        <div className="chat-input">
          <input
            type="text"
            value={userMessage}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder="Tapez votre question..."
          />
          <button onClick={handleSendMessage}>Envoyer</button>
        </div>
      </div>
    </div>
  );
};

export default Chatbot;
